<template>
  <v-dialog :value="value" @input="$emit('input', $event)" width="700">
    <v-card>
      <v-alert class="ma-0" :type="alertType" v-if="alert">{{
        alertMessage
      }}</v-alert>

      <v-card-title class="headline grey lighten-2">
        <span v-if="mode === 'list'">Referral Groups</span>
        <span v-else-if="editingId">Edit Group</span>
        <span v-else>New Group</span>
        <v-spacer></v-spacer>
        <v-btn v-if="mode === 'list'" color="primary" small @click="startCreate"
          >New Group</v-btn
        >
      </v-card-title>

      <v-card-text class="pt-4">
        <template v-if="mode === 'list'">
          <div
            v-if="!referralGroups || referralGroups.length === 0"
            class="text-body-2 grey--text py-4 text-center"
          >
            No groups yet. Create one to combine contacts into a single
            aggregate row.
          </div>
          <v-list v-else dense>
            <v-list-item
              v-for="group in referralGroups"
              :key="group.id"
              class="px-0"
            >
              <v-list-item-content>
                <v-list-item-title>{{ group.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ (group.members || []).length }}
                  {{
                    (group.members || []).length === 1 ? "contact" : "contacts"
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="flex-row">
                <v-icon small class="mr-3" @click="startEdit(group)"
                  >mdi-pencil</v-icon
                >
                <v-icon small color="red" @click="deleteGroup(group)"
                  >mdi-delete</v-icon
                >
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </template>

        <template v-else>
          <v-text-field
            v-model="formName"
            label="Group Name"
            placeholder="e.g. ABC Medical Network"
          ></v-text-field>
          <v-autocomplete
            v-model="formMemberKeys"
            :items="memberOptions"
            label="Contacts in this group"
            multiple
            chips
            small-chips
            deletable-chips
            :loading="$apollo.loading"
          ></v-autocomplete>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <template v-if="mode === 'list'">
          <v-btn text @click="close">Close</v-btn>
        </template>
        <template v-else>
          <v-btn text @click="backToList">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!formName || formMemberKeys.length === 0 || saving"
            :loading="saving"
            @click="saveGroup"
            >Save</v-btn
          >
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import gql from "graphql-tag";
import uniqBy from "lodash/uniqBy";

const REFERRAL_GROUPS_QUERY = gql`
  query ReferralGroups {
    referralGroups {
      id
      name
      members {
        saId
        saCtg
        name
      }
    }
  }
`;

export default {
  name: "ReferralGroupsManager",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    contactOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      referralGroups: [],
      mode: "list",
      editingId: null,
      formName: "",
      formMemberKeys: [],
      editingMembers: [],
      saving: false,
      alert: false,
      alertType: "error",
      alertMessage: ""
    };
  },
  apollo: {
    referralGroups: {
      query: REFERRAL_GROUPS_QUERY
    }
  },
  computed: {
    memberOptions() {
      let fromContacts = (this.contactOptions || [])
        .filter(c => c && c.saId != null && c.saCtg != null)
        .map(c => ({
          text: c.name,
          value: this.memberKey(c.saCtg, c.saId),
          saId: c.saId,
          saCtg: c.saCtg,
          name: c.name
        }));
      let fromEditing = (this.editingMembers || []).map(m => ({
        text: m.name,
        value: this.memberKey(m.saCtg, m.saId),
        saId: m.saId,
        saCtg: m.saCtg,
        name: m.name
      }));
      let combined = uniqBy([...fromContacts, ...fromEditing], "value");
      return combined.sort((a, b) =>
        (a.text || "").localeCompare(b.text || "")
      );
    },
    memberLookup() {
      let map = {};
      this.memberOptions.forEach(o => {
        map[o.value] = { sa_id: o.saId, sa_ctg: o.saCtg, name: o.name };
      });
      return map;
    }
  },
  methods: {
    memberKey(ctg, id) {
      return `${ctg}:${id}`;
    },
    startCreate() {
      this.editingId = null;
      this.formName = "";
      this.formMemberKeys = [];
      this.editingMembers = [];
      this.mode = "form";
    },
    startEdit(group) {
      this.editingId = parseInt(group.id, 10);
      this.formName = group.name;
      this.editingMembers = (group.members || []).map(m => ({
        saId: m.saId,
        saCtg: m.saCtg,
        name: m.name
      }));
      this.formMemberKeys = this.editingMembers.map(m =>
        this.memberKey(m.saCtg, m.saId)
      );
      this.mode = "form";
    },
    backToList() {
      this.mode = "list";
      this.editingId = null;
      this.formName = "";
      this.formMemberKeys = [];
      this.editingMembers = [];
    },
    close() {
      this.backToList();
      this.$emit("input", false);
    },
    showError(message) {
      this.alert = true;
      this.alertType = "error";
      this.alertMessage = message;
      setTimeout(() => {
        this.alert = false;
      }, 4000);
    },
    saveGroup() {
      let members = this.formMemberKeys
        .map(key => this.memberLookup[key])
        .filter(Boolean);

      this.saving = true;

      let mutation = this.editingId
        ? gql`
            mutation UpdateReferralGroup(
              $referralGroupId: Int!
              $referralGroup: ReferralGroupInput!
            ) {
              updateReferralGroup(
                referralGroupId: $referralGroupId
                referralGroup: $referralGroup
              ) {
                id
                name
                members {
                  saId
                  saCtg
                  name
                }
              }
            }
          `
        : gql`
            mutation CreateReferralGroup($referralGroup: ReferralGroupInput!) {
              createReferralGroup(referralGroup: $referralGroup) {
                id
                name
                members {
                  saId
                  saCtg
                  name
                }
              }
            }
          `;

      let variables = {
        referralGroup: { name: this.formName, members }
      };
      if (this.editingId) {
        variables.referralGroupId = this.editingId;
      }

      this.$apollo
        .mutate({
          mutation,
          variables,
          update: () => {
            this.$apollo.queries.referralGroups.refetch();
          }
        })
        .then(() => {
          this.saving = false;
          this.backToList();
          this.$emit("groups-changed");
        })
        .catch(error => {
          this.saving = false;
          console.error(error);
          this.showError(error.message);
        });
    },
    deleteGroup(group) {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation DeleteReferralGroup($referralGroupId: Int!) {
              deleteReferralGroup(referralGroupId: $referralGroupId) {
                id
              }
            }
          `,
          variables: {
            referralGroupId: parseInt(group.id, 10)
          },
          update: () => {
            this.$apollo.queries.referralGroups.refetch();
          }
        })
        .then(() => {
          this.$emit("groups-changed");
        })
        .catch(error => {
          console.error(error);
          this.showError(error.message);
        });
    }
  }
};
</script>

<style scoped>
.flex-row {
  flex-direction: row;
}
</style>
